/** @format */
import { Trans, useTranslation } from 'next-i18next';
import React, { ChangeEvent, useContext, useState } from 'react';

import { getConfig } from '@toucan/helpers/settings';

import {
    ApplicationContext,
    ModalSize,
} from '@toucan/client/global/application';
import { MarketContext } from '@toucan/client/global/market';
import { Link } from '@toucan/client/shared/components/link';

import { ExplicitConsentTermsAndConditions } from './components/explicit-consent-checkbox';
import { TermsAndConditionsModalContent } from './components/terms-and-conditions-modal-content';
import {
    UseTermsAndConditionsProps,
    UseTermsAndConditionsReturnType,
} from './types';

/**
 * A hook used to display the terms and conditions checkbox used in certain markets
 * NOTE: Markets are set on EXPLICIT_TERMS_CONDITIONS_MARKETS env (comma seperated)
 */
export const useTermsAndConditions = ({
    isSubmitting,
    className,
    enabled = true,
}: UseTermsAndConditionsProps): UseTermsAndConditionsReturnType => {
    const { publicRuntimeConfig } = getConfig();
    const { t } = useTranslation();
    const { activeMarket } = useContext(MarketContext);
    const [isChecked, setIsChecked] = useState<boolean>(false);
    const { explicitTermsConditionsMarkets } = publicRuntimeConfig;
    const { setModalContent, setModalVisible } = useContext(ApplicationContext);
    const requiresExplicitConsent = explicitTermsConditionsMarkets.includes(
        activeMarket.code
    );

    function handleCheckboxChange(event: ChangeEvent<HTMLInputElement>) {
        setIsChecked(event.currentTarget.checked);
    }

    function openTermsHandler() {
        setModalContent(<TermsAndConditionsModalContent />);
        setModalVisible(true, {
            size: ModalSize.MEDIUM,
        });
    }

    if (!enabled) {
        return {
            disableSubmitButton: isSubmitting,
            termsAndConditionsNode: null,
        };
    }

    const disableSubmitButton =
        (requiresExplicitConsent && !isChecked) || isSubmitting;

    // if market is in explicit terms and conditions it means
    // that they need to check the terms and conditions checkbox.
    // otherwise they will be displayed generic terms & conditions copy
    // without a checkbox
    const termsAndConditionsNode = requiresExplicitConsent ? (
        <ExplicitConsentTermsAndConditions
            openTermsHandler={openTermsHandler}
            isChecked={isChecked}
            onChange={handleCheckboxChange}
            className={className}
        />
    ) : (
        <Trans
            components={{
                ['terms-page-link']: (
                    <Link
                        onClick={openTermsHandler}
                        data-testid="terms-and-conditions__open-modal-button"
                    />
                ),
            }}
        >
            {t(
                'By completing the transaction, you agree to our <terms-page-link>Terms & Conditions</terms-page-link>'
            )}
        </Trans>
    );

    return {
        disableSubmitButton,
        termsAndConditionsNode,
    };
};
